import { lazy, Suspense } from 'react';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';

import UnifiedLayout from '#root/layouts/Unified';
import * as ROUTES from '#root/constants/navigation';
import {
  AuthValidate,
  AuthDebug,
  NotFound,
  TravelerDashboard,
  TravelerOrders,
  TravelerOffers,
  TravelerTraining,
  TravelerTrips,
  TravelerDeals,
  TravelerDealEdit,
  TravelerDealNew,
  TravelerDealDetail,
  TravelerDealAuto,
  TravelerEarnings,
  TravelerDeliverySetup,
  UserAddress,
  UserChat,
  UserProfile,
  UserShipments,
  UserWallet,
  UserWalletRefunds,
  UserTravelerSettings,
  TravelerTripNew,
  UserCoupons,
  UserDealDetailView,
  UserOrders,
  UserOrderDetail,
  UserOrdersStore,
} from '#root/views';

import AuthRouteWithLayout from './AuthRouteWithLayout';

// Rutas /test/<nombre> solo en `npm run dev`: en el build DEV es false y Rollup descarta el import()
const DevShowcaseRoute = import.meta.env.DEV ? lazy(() => import('./DevShowcaseRoute')) : null;

const Routes = () => {
  return (
    <BrowserRouter>
      <Switch>
        <Route exact path={ROUTES.AUTH_START} component={AuthValidate} />
        <Route exact path={ROUTES.AUTH_DEBUG} component={AuthDebug} />
        {DevShowcaseRoute && (
          <Route path={ROUTES.DEV_SHOWCASE}>
            <Suspense fallback={null}>
              <DevShowcaseRoute />
            </Suspense>
          </Route>
        )}
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerDashboard}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_DASHBOARD}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerTrips}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_TRIPS}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerTripNew}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_TRIPS_NEW}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerTraining}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_TRAINING}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerDeliverySetup}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_DELIVERY_SETUP}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerOrders}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_ORDERS}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerOrders}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_ORDERS_BY_ID}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerOffers}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_OFFERS}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerOffers}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_OFFERS_BY_ID}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerDeals}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_DEALS}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerDeals}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_DEALS_CREATED}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerDeals}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_DEALS_DELIVERED}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerDeals}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_DEALS_CANCELED}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerDealDetail}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_DEAL_DETAIL}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerDealEdit}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_DEAL_EDIT}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerDealNew}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_DEAL_NEW}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerDealAuto}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_DEAL_AUTO}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerDealDetail}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_DEAL_DETAIL_RATING}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerEarnings}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_EARNINGS}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerEarnings}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_EARNINGS_UPCOMING}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerEarnings}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_EARNINGS_REQUESTED}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero']}
          component={TravelerEarnings}
          exact
          layout={UnifiedLayout}
          path={ROUTES.TRAVELER_EARNINGS_PAID}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_ORDERS}
          layout={UnifiedLayout}
          component={UserOrders}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_ORDERS_BY_ID}
          layout={UnifiedLayout}
          component={UserOrderDetail}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_ORDERS_BY_ID_BY_OFFER}
          layout={UnifiedLayout}
          component={UserOrderDetail}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_DEALS}
          layout={UnifiedLayout}
          component={UserOrders}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_DEALS_TRANSIT}
          layout={UnifiedLayout}
          component={UserOrders}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_DEALS_DELIVERED}
          layout={UnifiedLayout}
          component={UserOrders}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_DEALS_INACTIVE}
          layout={UnifiedLayout}
          component={UserOrders}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_DEAL_DETAIL}
          layout={UnifiedLayout}
          component={UserDealDetailView}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_DEAL_DETAIL_RATING}
          layout={UnifiedLayout}
          component={UserDealDetailView}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_ORDERS_STORE}
          layout={UnifiedLayout}
          component={UserOrdersStore}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_ADDRESS}
          layout={UnifiedLayout}
          component={UserAddress}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_CHAT}
          layout={UnifiedLayout}
          component={UserChat}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_CHAT_ROOM}
          layout={UnifiedLayout}
          component={UserChat}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_PROFILE}
          layout={UnifiedLayout}
          component={UserProfile}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_WALLET}
          layout={UnifiedLayout}
          component={UserWallet}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_WALLET_REFUNDS}
          layout={UnifiedLayout}
          component={UserWalletRefunds}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_SHIPMENTS}
          layout={UnifiedLayout}
          component={UserShipments}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_TRAVELER_SETTINGS}
          layout={UnifiedLayout}
          component={UserTravelerSettings}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          path={ROUTES.USER_COUPONS}
          layout={UnifiedLayout}
          component={UserCoupons}
        />
        <AuthRouteWithLayout
          allowedRoles={['viajero', 'comprador']}
          exact
          component={NotFound}
          layout={UnifiedLayout}
          path={ROUTES.NOT_FOUND}
        />
        <Redirect from="*" to={ROUTES.AUTH_START} />
      </Switch>
    </BrowserRouter>
  );
};

export default Routes;
